// SceneSlot.tsx — operator-UI §4b (2026-09-30): ONE animated slot per scene, not its start frame, end frame and card side by
// side. The clip when it exists (a muted loop); else start → end frame as a short browser-drawn crossfade with a push-in; a
// presenter scene as its image with a sway; a card as the card. A tap plays the scene's voice slice; a start ↔ end toggle
// for inspection. Reduced motion → the frames still. ONE component for the operator and the customer: built here first,
// then moved into @vms/creation-ui. The decision of what animates is `slotOf` — pure, so both copies decide alike.
import React from "react";
import type { Filmstrip, FilmstripBeat } from "../../api/ops";
import { voiceSliceOf } from "./Filmstrip";
import { slotOf, windowsOf, beatAt } from "./scene-slot-rules";
export { slotOf, windowsOf, beatAt, type Slot } from "./scene-slot-rules";

const CSS = `
@keyframes ss-pushin { from { transform: scale(1); } to { transform: scale(1.08); } }
@keyframes ss-fadeout { 0%, 35% { opacity: 1; } 65%, 100% { opacity: 0; } }
@keyframes ss-sway { 0%, 100% { transform: rotate(-0.6deg) translateX(-1%); } 50% { transform: rotate(0.6deg) translateX(1%); } }
.ss-anim-pushin { animation: ss-pushin 3s ease-in-out infinite alternate; }
.ss-anim-fadeout { animation: ss-fadeout 3s ease-in-out infinite alternate; }
.ss-anim-sway { animation: ss-sway 4s ease-in-out infinite; transform-origin: 50% 90%; }
@media (prefers-reduced-motion: reduce) { .ss-anim-pushin, .ss-anim-fadeout, .ss-anim-sway { animation: none; } }
`;
let injected = false;
function useSlotCss() {
  React.useEffect(() => {
    if (injected || typeof document === "undefined") return;
    const el = document.createElement("style"); el.textContent = CSS; document.head.appendChild(el); injected = true;
  }, []);
}

const fill: React.CSSProperties = { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" };

export function SceneSlot(p: { beat: FilmstripBeat & { shotType?: string | null }; voice?: Filmstrip["document"]["voice"] }) {
  useSlotCss();
  const slot = slotOf(p.beat);
  const [inspect, setInspect] = React.useState(false);
  const audio = React.useRef<HTMLAudioElement | null>(null);
  const slice = p.voice ? voiceSliceOf(p.beat, p.voice) : null;
  const play = () => {
    if (!slice) return;
    if (!audio.current) audio.current = new Audio(slice);
    const a = audio.current;
    if (a.paused) { a.currentTime = 0; void a.play().catch(() => {}); } else a.pause();
  };
  React.useEffect(() => () => { audio.current?.pause(); }, []);
  const label = `${p.beat.beatId} — ${slot.kind === "crossfade" ? "start → end" : slot.kind}${slice ? " · tap to hear its line" : ""}`;
  return (
    <div style={{ position: "relative", width: "100%", height: "100%", overflow: "hidden", cursor: slice ? "pointer" : "default" }}
      onClick={play} role={slice ? "button" : undefined} aria-label={label} title={label}>
      {slot.kind === "clip" && <video src={slot.url} poster={slot.poster ?? undefined} muted loop autoPlay playsInline style={fill} />}
      {slot.kind === "presenter" && <img src={slot.image} alt="" className="ss-anim-sway" style={fill} />}
      {slot.kind === "card" && <img src={slot.url} alt="" style={{ ...fill, objectFit: "contain" }} />}
      {slot.kind === "frame" && <img src={slot.url} alt="" className="ss-anim-pushin" style={fill} />}
      {slot.kind === "crossfade" && !inspect && (
        <>
          <img src={slot.end} alt="" className="ss-anim-pushin" style={fill} />
          <img src={slot.start} alt="" className="ss-anim-fadeout" style={fill} />
        </>)}
      {slot.kind === "crossfade" && inspect && (
        <div style={{ ...fill, display: "flex" }}>
          <img src={slot.start} alt="start frame" style={{ width: "50%", height: "100%", objectFit: "cover" }} />
          <img src={slot.end} alt="end frame" style={{ width: "50%", height: "100%", objectFit: "cover" }} />
        </div>)}
      {slot.kind === "none" && <div style={{ padding: 8, color: "#9ca3af" }}>nothing yet</div>}
      {slot.kind === "crossfade" && (
        <button onClick={(e) => { e.stopPropagation(); setInspect((v) => !v); }} aria-pressed={inspect}
          style={{ position: "absolute", top: 4, right: 4, fontSize: 10, padding: "1px 5px", borderRadius: 4, border: "1px solid #d1d5db", background: "#ffffffd9" }}>
          {inspect ? "play" : "start ↔ end"}
        </button>)}
    </div>
  );
}

/** The whole rough cut (production.animaticUrl) in one player; reports the scene playing so the filmstrip follows it. */
export function RoughCutPlayer(p: { url: string; beats: readonly FilmstripBeat[]; onActive: (beatId: string | null) => void }) {
  const windows = React.useMemo(() => windowsOf(p.beats), [p.beats]);
  const last = React.useRef<string | null>(null);
  const tick = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const id = beatAt(windows, e.currentTarget.currentTime * 1000);
    if (id !== last.current) { last.current = id; p.onActive(id); }
  };
  return <video src={p.url} controls onTimeUpdate={tick} onSeeked={tick} onEnded={() => { last.current = null; p.onActive(null); }}
    style={{ maxHeight: 360, borderRadius: 6, background: "#000" }} />;
}
